import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { aiToolsApi } from "@/shared/api/endpoints";
import type { LocalizedText } from "@/shared/api/types";
import { Button, type LocalizedInputActionProps } from "@/shared/ui";
import { aiErrorMessage } from "./AiErrorMessage";
import { AI_TOOLS } from "./keys";
import { useAiAvailable } from "./useAi";

const filled = (v: LocalizedText, loc: string) => !!v[loc]?.trim();

/**
 * "Dịch tự động" next to a LocalizedInput: fills the empty locales from a
 * filled one (Vietnamese first). The text lands in the inputs only, so the
 * editor reviews it before saving the form. Hidden when the feature is off,
 * when nothing is filled or nothing is missing.
 */
export function AiTranslateAction({ label, value, onChange, locales }: LocalizedInputActionProps) {
  const ai = useAiAvailable(AI_TOOLS.translateText);
  // The editor may keep typing while the request runs: merge into the latest value.
  const latest = useRef(value);
  useEffect(() => { latest.current = value; });
  const source = locales.includes("vi") && filled(value, "vi") ? "vi" : locales.find((l) => filled(value, l));
  const targets = locales.filter((l) => !filled(value, l));
  const translate = useMutation({
    mutationFn: () => aiToolsApi.translate({
      texts: { text: value[source!]!.trim() }, source_locale: source!, target_locales: targets, context: label,
    }),
    onSuccess: (res) => {
      const next = { ...latest.current };
      for (const loc of targets) {
        const text = res.translations[loc]?.text;
        if (text && !filled(next, loc)) next[loc] = text;
      }
      onChange(next);
    },
  });
  if (ai.isLoading || (ai.known && !ai.available) || !source || !targets.length) return null;
  return (
    <span className="row gap-xs">
      {translate.error ? <span className="field-error xsmall" role="alert">{aiErrorMessage(translate.error)}</span> : null}
      <Button size="sm" variant="ghost" icon="sparkles" loading={translate.isPending} onClick={() => translate.mutate()}
        title={`Dịch “${label}” sang ${targets.join(", ")} bằng AI — xem lại trước khi lưu`}>
        Dịch tự động
      </Button>
    </span>
  );
}
